import React, { useCallback, useState } from 'react';

import moment from 'moment';
import momentTimezone from 'moment-timezone';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  LabelList,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import tailwindConfig from 'stylesheets/tailwind.config';
import voca from 'voca';

import { useProjectsAnalyticsProjectsCreatedReportQuery as useProjectsCreatedReportQuery } from 'components/hooks/projectsAnalyticsProjectsCreatedReportQueries';
import { useInfiniteProjectsAnalyticsProjectsReportsProjectsCreatedReportsDashboardReportQuery as useInfiniteProjectsCreatedReportsDashboardReportQuery } from 'components/hooks/projectsAnalyticsProjectsReportsProjectsCreatedReportsDashboardReportQueries';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectsAnalyticsDashboardContext } from 'components/contexts/ProjectsAnalyticsDashboardContext';
import Card from 'components/projectsAnalytics/components/Card';
import NoDataMessage from 'components/projectsAnalytics/components/NoDataMessage';
import ProjectsModal from 'components/projectsAnalytics/components/ProjectsModal';
import ReportHeader from 'components/projectsAnalytics/components/ReportHeader';
import {
  defaultChartProps,
  generateChartProps,
} from 'components/projectsAnalytics/helpers/projectsCreatedCard';
import useFilters from 'components/projectsAnalytics/hooks/useFilters';
import useKeyHighlight from 'components/projectsAnalytics/hooks/useKeyHighlight';
import useProjectsModal from 'components/projectsAnalytics/hooks/useProjectsModal';

import { formatDate } from 'components/helpers/dates';
import { pluralizeProjectsName } from 'components/helpers/resources/projects';

const { colors } = tailwindConfig.theme;

const dateRangeValueKeyMap = {
  this_year: 'thisYear',
  last_year: 'lastYear',
  all_time: 'allTime',
};

const roundedTopBarRadii = [2, 2, 0, 0];

export default function ProjectsCreatedCard({
  dashboardFilterParams,
  isGeneratingExport,
  onExportClick,
}) {
  const currentActor = useCurrentActor();

  const { filters: cardFilters, handleFilterChange: handleCardFilterChange } =
    useFilters({ includeDeleted: false });

  const projectsAnalyticsDashboardContext =
    useProjectsAnalyticsDashboardContext();

  const {
    domainDashboardConfigurableFields,
    isApprovalsEnabled,
    isDigitalSignaturesEnabled,
    isFoldersEnabled,
    isProjectReviewDatesEnabled,
    isRiskRegisterEnabled,
  } = projectsAnalyticsDashboardContext;

  const { closeProjectsModal, openProjectsModal, projectsModal } =
    useProjectsModal({
      domainDashboardConfigurableFields,
      isApprovalsEnabled,
      isDigitalSignaturesEnabled,
      isFoldersEnabled,
      isProjectReviewDatesEnabled,
      isRiskRegisterEnabled,
    });

  const [dateRange, setDateRange] = useState('this_year');

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectsAnalytics.components.ProjectsCreatedCard',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const {
    data: projectsCreatedReport,
    isSuccess: isProjectsCreatedReportSuccess,
  } = useProjectsCreatedReportQuery({
    params: {
      filter: {
        date_range: dateRange,
        include_deleted: cardFilters.includeDeleted,
        ...dashboardFilterParams,
      },
    },
    queryOptions: { keepPreviousData: true },
  });

  const totalCount =
    isProjectsCreatedReportSuccess ?
      projectsCreatedReport.data.attributes.countBreakdown.reduce(
        (totalCount, { project_count: projectCount }) =>
          totalCount + projectCount,
        0,
      )
    : 0;

  const chartProps =
    isProjectsCreatedReportSuccess ?
      generateChartProps({ report: projectsCreatedReport })
    : defaultChartProps;

  const {
    data: domainDashboardReport,
    fetchNextPage: fetchDomainDashboardReportNextPage,
    hasNextPage: hasDomainDashboardReportNextPage,
  } = useInfiniteProjectsCreatedReportsDashboardReportQuery({
    params: {
      filter: projectsModal.filters,
      selections: projectsModal.selections,
    },
    queryOptions: { enabled: projectsModal.isOpen },
  });

  const { pluralRAMSName } = currentActor.division.attributes;

  const handleExportClick = () => {
    onExportClick({ params: { filter: projectsModal.filters } });
  };

  const handleTotalCountClick = () => {
    const london = momentTimezone.tz('Europe/London');

    let formattedDate;
    let createdAtFilter;

    switch (dateRange) {
      case 'this_year':
        formattedDate = `${london.clone().year()}`;
        createdAtFilter = {
          gte: london.clone().startOf('year').format(),
          lte: london.clone().endOf('year').format(),
        };
        break;
      case 'last_year':
        formattedDate = `${london.clone().subtract(1, 'year').year()}`;
        createdAtFilter = {
          gte: london.clone().subtract(1, 'year').startOf('year').format(),
          lte: london.clone().subtract(1, 'year').endOf('year').format(),
        };
        break;
      case 'all_time':
        formattedDate = `${moment(projectsCreatedReport.meta.accountCreatedAt).year()} - ${london.clone().year()}`;
        createdAtFilter = {};
        break;
    }

    const projectsNameWithCount = pluralizeProjectsName({
      count: totalCount,
      currentActor,
      shouldIncludeCount: true,
    });

    openProjectsModal({
      filters: {
        creation_date: createdAtFilter,
        ...(dashboardFilterParams.division_ids && {
          company_id: { in: dashboardFilterParams.division_ids },
        }),
      },
      subtitle:
        cardFilters.includeDeleted ?
          i18nT('subtitle.including', {
            deletedCount,
            interpolation: { escapeValue: false },
            projectsNameWithCount,
            ramsName: pluralizeProjectsName({
              count: deletedCount,
              currentActor,
            }),
          })
        : projectsNameWithCount,
      title: `${i18nT('title', {
        interpolation: { escapeValue: false },
        pluralRAMSName: voca.capitalize(pluralRAMSName),
      })}: ${formattedDate}`,
    });

    projectsAnalyticsDashboardContext.sendAnalytics(
      'RAMS analytics: segment click',
      {
        chart: 'Projects created',
        filters: cardFilters,
        segment: formattedDate,
      },
    );
  };

  const handleProjectClick = (project) => {
    projectsAnalyticsDashboardContext.sendAnalytics(
      'RAMS analytics: project click',
      {
        chart: 'Projects created',
        filters: cardFilters,
        project: project.attributes.name,
      },
    );
  };

  const dateRangeOptions = Object.entries(dateRangeValueKeyMap).reduce(
    (acc, [value, key]) => {
      acc.push({ label: i18nTShared(`periods.${key}`), value });
      return acc;
    },
    [],
  );

  const deletedCount = projectsCreatedReport?.data.attributes.deletedCount;

  return (
    <Card>
      <ReportHeader
        dropdownOptions={dateRangeOptions}
        dropdownValue={{
          label: i18nTShared(`periods.${dateRangeValueKeyMap[dateRange]}`),
          value: dateRange,
        }}
        filters={[
          {
            count: deletedCount,
            label: i18nT('filters.includeDeleted', {
              interpolation: { escapeValue: false },
              pluralRAMSName,
            }),
            name: 'includeDeleted',
            value: cardFilters.includeDeleted,
          },
        ]}
        onFilterChange={handleCardFilterChange}
        onSelectionChange={(selectedOption) => {
          setDateRange(selectedOption.value);
        }}
        onTotalCountClick={totalCount > 0 ? handleTotalCountClick : null}
        subtitle={i18nT(
          `subtitle.${cardFilters.includeDeleted ? 'includes' : 'excludes'}`,
          { interpolation: { escapeValue: false }, pluralRAMSName },
        )}
        title={i18nT('title', {
          interpolation: { escapeValue: false },
          pluralRAMSName: voca.capitalize(pluralRAMSName),
        })}
        totalCount={totalCount > 0 ? totalCount : null}
      />
      {projectsCreatedReport &&
        (totalCount > 0 ?
          <ReportBody
            cardFilters={cardFilters}
            chartProps={chartProps}
            dashboardFilterParams={dashboardFilterParams}
            dateRange={dateRange}
            openProjectsModal={openProjectsModal}
          />
        : <NoDataMessage message={i18nTShared('checkAppliedFilters')} />)}
      {projectsModal.isOpen && (
        <ProjectsModal
          domainDashboardReport={domainDashboardReport}
          hasNextPage={hasDomainDashboardReportNextPage}
          isGeneratingExport={isGeneratingExport}
          onClose={closeProjectsModal}
          onExportClick={handleExportClick}
          onFetchNextPage={fetchDomainDashboardReportNextPage}
          onProjectClick={handleProjectClick}
          selections={projectsModal.selections}
          subtitle={projectsModal.subtitle}
          title={projectsModal.title}
        />
      )}
    </Card>
  );
}

ProjectsCreatedCard.propTypes = {
  dashboardFilterParams: PropTypes.shape({
    division_ids: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  isGeneratingExport: PropTypes.bool.isRequired,
  onExportClick: PropTypes.func.isRequired,
};

function ReportBody({
  cardFilters,
  chartProps,
  dashboardFilterParams,
  dateRange,
  openProjectsModal,
}) {
  const currentActor = useCurrentActor();

  const { handleKeyHighlight, handleKeyUnhighlight, highlightedKey } =
    useKeyHighlight(null);

  const projectsAnalyticsDashboardContext =
    useProjectsAnalyticsDashboardContext();

  const [barPosition, setBarPosition] = useState({});

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectsAnalytics.components.ProjectsCreatedCard',
  });

  const handleSegmentClick = ({ entry }) => {
    const formattedDate = moment(entry.yearMonth).format('MMMM YYYY');
    const { count, deletedCount, yearMonth } = entry;

    const createdAtFilter = {
      gte: moment.parseZone(yearMonth).startOf('month').format(),
      lte: moment.parseZone(yearMonth).endOf('month').format(),
    };

    const projectsNameWithCount = pluralizeProjectsName({
      count,
      currentActor,
      shouldIncludeCount: true,
    });

    openProjectsModal({
      filters: {
        creation_date: createdAtFilter,
        ...(dashboardFilterParams.division_ids && {
          company_id: { in: dashboardFilterParams.division_ids },
        }),
      },
      subtitle:
        cardFilters.includeDeleted ?
          i18nT('subtitle.including', {
            deletedCount,
            projectsNameWithCount,
            ramsName: pluralizeProjectsName({
              count: deletedCount,
              currentActor,
            }),
          })
        : projectsNameWithCount,
      title: `${i18nT('title', {
        interpolation: { escapeValue: false },
        pluralRAMSName: voca.capitalize(
          currentActor.division.attributes.pluralRAMSName,
        ),
      })}: ${formatDate({ date: yearMonth, format: 'MMMM yyyy' })}`,
    });

    handleKeyUnhighlight();

    projectsAnalyticsDashboardContext.sendAnalytics(
      'RAMS analytics: segment click',
      {
        chart: 'Projects created',
        segment: formattedDate,
        filters: cardFilters,
      },
    );
  };

  return (
    <div className='tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-pb-5 tw-pl-6 tw-pr-6'>
      <BarChart
        data={chartProps.data}
        height={164}
        margin={{ top: 16, right: 0, left: 0, bottom: 16 }}
        onMouseLeave={() => handleKeyUnhighlight()}
        width={512}
      >
        <CartesianGrid stroke={colors.grey[100]} vertical={false} />
        <Tooltip
          allowEscapeViewBox={{ x: true, y: true }}
          content={
            dateRange === 'all_time' ?
              <CustomizedTooltip
                isTooltipVisible={Object.keys(barPosition).length !== 0}
              />
            : () => null
          }
          cursor={false}
          isAnimationActive={false}
          position={{ x: barPosition.x, y: barPosition.y - 12 }}
        />
        <Bar cursor='pointer' dataKey='count'>
          <LabelList
            content={dateRange === 'all_time' ? () => null : CustomizedBarLabel}
            dataKey='count'
          />
          {chartProps.data.map((entry) => {
            const isOtherBarHighlighted =
              highlightedKey && entry.countKey !== highlightedKey;
            const barFill =
              isOtherBarHighlighted ? colors.grey[100] : colors.cyan[700];

            return (
              <Cell
                fill={barFill}
                key={entry.countKey}
                onClick={() => handleSegmentClick({ entry })}
                onMouseEnter={(data) => {
                  handleKeyHighlight(entry.countKey);
                  setBarPosition({
                    x: data.target.getAttribute('x'),
                    y: data.target.getAttribute('y'),
                  });
                }}
                onMouseLeave={() => setBarPosition({})}
                radius={roundedTopBarRadii}
              />
            );
          })}
        </Bar>
        <XAxis
          dataKey='name'
          padding={{ left: 32, right: 4 }}
          stroke={colors.grey[400]}
          style={{
            fontSize: '12px',
            fontFamily: 'Inter',
            fontWeight: '400',
            color: colors.grey[100],
          }}
          tick={
            dateRange === 'all_time' ? false : (
              {
                fill: colors.grey[500],
                fontFamily: 'Inter',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 400,
                dy: 4,
              }
            )
          }
          tickLine={false}
          width={512}
        >
          <Label
            offset={dateRange === 'all_time' ? -12 : 4}
            position='bottom'
            style={{
              fill: colors.grey[500],
              fontFamily: 'Inter',
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: 400,
            }}
            value={chartProps.xAxisLabel}
          />
        </XAxis>
        <YAxis
          allowDecimals={false}
          axisLine={false}
          mirror={true}
          tick={CustomizedYAxisTick}
          tickLine={false}
          width={32}
        />
      </BarChart>
    </div>
  );
}

ReportBody.propTypes = {
  chartProps: PropTypes.shape({
    data: PropTypes.array.isRequired,
    xAxisLabel: PropTypes.node.isRequired,
  }),
  cardFilters: PropTypes.object.isRequired,
  dashboardFilterParams: PropTypes.shape({
    division_ids: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  dateRange: PropTypes.string.isRequired,
  openProjectsModal: PropTypes.func.isRequired,
};

function CustomizedTooltip({ isTooltipVisible, payload }) {
  const currentActor = useCurrentActor();

  const [tooltipSize, setTooltipSize] = useState({});

  const ref = useCallback((node) => {
    setTooltipSize(
      node !== null ?
        { width: node.offsetWidth, height: node.offsetHeight }
      : {},
    );
  }, []);

  if (isTooltipVisible && payload?.length > 0) {
    const { count, yearMonth } = payload[0].payload;

    return (
      <div
        className='tooltip-dark tw-m-0 tw-w-24 tw-rounded-md'
        ref={ref}
        style={{
          transform: `translate(-${tooltipSize.width / 2 + 10}px,-${tooltipSize.height}px)`,
        }}
      >
        <div
          className='tooltip-dark-arrow'
          data-placement='top'
          style={{ left: `${tooltipSize.width / 2 - 6}px` }}
        />
        <p className='tw-m-0'>
          {formatDate({ date: yearMonth, format: 'MMM yyyy' })}
        </p>
        <p className='tw-m-0'>
          {pluralizeProjectsName({
            count,
            currentActor,
            shouldIncludeCount: true,
          })}
        </p>
      </div>
    );
  }

  return null;
}

CustomizedTooltip.propTypes = {
  isTooltipVisible: PropTypes.bool.isRequired,
  payload: PropTypes.array,
};

function CustomizedBarLabel({ value, viewBox, width }) {
  return (
    <g>
      <foreignObject
        height={16}
        width={width}
        x={viewBox.x}
        y={viewBox.y && viewBox.y - 16}
      >
        <div className='tw-flex tw-justify-center tw-text-s tw-font-normal tw-tracking-wide tw-text-grey-500'>
          {value === 0 ? null : value}
        </div>
      </foreignObject>
    </g>
  );
}

CustomizedBarLabel.propTypes = {
  viewBox: PropTypes.object.isRequired,
  value: PropTypes.node,
  width: PropTypes.number.isRequired,
};

function CustomizedYAxisTick({ payload, y }) {
  return (
    <g>
      <foreignObject height={16} width={32} x={0} y={y - 16}>
        <div className='tw-flex tw-text-s tw-font-normal tw-tracking-wide tw-text-grey-500'>
          {payload.value.toLocaleString()}
        </div>
      </foreignObject>
    </g>
  );
}

CustomizedYAxisTick.propTypes = {
  payload: PropTypes.array.isRequired,
  y: PropTypes.number.isRequired,
};
